import { useEffect, useRef, useState } from 'react';

interface OptimizedImageProps {
    src: string;
    fallbackSrc?: string;
    alt: string;
    className?: string;
    loading?: 'lazy' | 'eager';
    priority?: boolean;
    width?: number;
    height?: number;
    sizes?: string;
    placeholder?: string;
}

export function OptimizedImage({
    src,
    fallbackSrc,
    alt,
    className = '',
    loading = 'lazy',
    priority = false,
    width,
    height,
    sizes = '100vw',
    placeholder: _placeholder,
}: OptimizedImageProps) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isInView, setIsInView] = useState(priority);
    const [currentSrc, setCurrentSrc] = useState(src);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        setCurrentSrc(src);
        setHasError(false);
        setIsLoaded(false);
    }, [src]);

    useEffect(() => {
        if (priority) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1, rootMargin: '50px' },
        );

        if (imgRef.current) {
            observer.observe(imgRef.current);
        }

        return () => observer.disconnect();
    }, [priority]);

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = () => {
        if (fallbackSrc && currentSrc !== fallbackSrc) {
            setCurrentSrc(fallbackSrc);
            setIsLoaded(false);
            return;
        }

        setHasError(true);
    };

    if (hasError) {
        return (
            <div className={`flex items-center justify-center bg-gray-200 ${className}`}>
                <span className="text-sm text-gray-500">Imagem não disponível</span>
            </div>
        );
    }

    return (
        <div className={`relative overflow-hidden ${className}`} ref={imgRef}>
            {/* Placeholder/Loading state */}
            {!isLoaded && <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />}

            {/* Actual image */}
            {isInView && (
                <img
                    src={currentSrc}
                    alt={alt}
                    className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
                    loading={priority ? 'eager' : loading}
                    decoding="async"
                    onLoad={handleLoad}
                    onError={handleError}
                    width={width}
                    height={height}
                    sizes={sizes}
                    fetchPriority={priority ? 'high' : 'auto'}
                />
            )}
        </div>
    );
}
